import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-10";
const ANSWER =
  "Ein Shopify-Shop besteht aus zwei Kostenblöcken: laufende Kosten aus Plattformtarif, Zahlungsgebühren je Bestellung und Apps sowie die einmalige Umsetzung. Die Umsetzung schwankt am stärksten und hängt vom Umfang ab: Anpassungen und Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung startet bei 3.990 € netto.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/was-kostet-ein-shopify-shop";
const TITLE = "Was kostet ein Shopify-Shop? Kurze, klare Antwort";
const DESCRIPTION =
  "Was ein Shopify-Shop kostet: Plattformgebühr, Zahlungsgebühren, Apps und die einmalige Umsetzung. Übersicht der Posten und was den Preis wirklich treibt.";

const faqs = [
  {
    q: "Was kostet ein Shopify-Shop insgesamt?",
    a: "Es gibt zwei Blöcke: laufende Kosten aus Plattformtarif, Zahlungsgebühren pro Bestellung und den eingesetzten Apps, dazu die einmalige Umsetzung. Die Umsetzung schwankt je nach Sortiment, Sprachen und Sonderlogik am stärksten.",
  },
  {
    q: "Was kostet die Umsetzung bei einem Freelancer?",
    a: "Bei mir Festpreis nach Umfang für einen neuen Shop, 110 € netto pro Stunde für Anpassungen und Entwicklung, Conversion-Optimierung ab 3.990 € netto. Den Preis bekommen Sie schriftlich, bevor gebaut wird.",
  },
  {
    q: "Welche Kosten werden am häufigsten unterschätzt?",
    a: "Apps. Einzeln wirken sie günstig, in Summe sind sie oft der größte laufende Posten und bremsen zusätzlich die Ladezeit. Zweiter Punkt sind Produktdaten und Bilder, deren Aufbereitung Zeit kostet.",
  },
  {
    q: "Was macht ein Projekt teurer?",
    a: "Viele Varianten, mehrere Sprachen oder Länder, B2B-Preise, Schnittstellen zu Warenwirtschaft oder Versand und individuelle Konfiguratoren. Ein klar umrissenes Standardsortiment ist deutlich günstiger.",
  },
  {
    q: "Wie senke ich die Kosten sinnvoll?",
    a: "Vollständige Produktdaten, Bilder und Texte selbst liefern, auf ein solides Theme statt einer Individualentwicklung setzen und Sonderwünsche erst nach dem Start umsetzen, wenn sie sich als nötig erweisen.",
  },
];

export const Route = createFileRoute("/fragen/was-kostet-ein-shopify-shop")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Kosten",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Was kostet ein Shopify-Shop?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Kosten"
      title="Was kostet ein Shopify-Shop?"
      intro="Kurz gesagt: laufende Kosten aus Tarif, Zahlungsgebühren und Apps, dazu die einmalige Umsetzung. Was der Shop am Ende kostet, entscheidet vor allem der Umfang, nicht die Plattform."
      source="frage-shopify-kosten"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Die laufenden Kosten",
          p: [
            "Die Plattformgebühr richtet sich nach dem gewählten Tarif. Dazu kommen Zahlungsgebühren pro Bestellung und die monatlichen Kosten der Apps. Die tatsächliche Summe hängt fast immer davon ab, wie viele Apps im Einsatz sind.",
          ],
          list: [
            "Plattformtarif, monatlich oder jährlich",
            "Zahlungsgebühren je Bestellung",
            "Apps, monatlich je Funktion",
            "Domain und gegebenenfalls E-Mail-Versand",
          ],
        },
        {
          h: "Die einmalige Umsetzung",
          p: [
            "Hier liegt die größte Spanne. Ein klar umrissener Shop mit solidem Theme, sauber angepasst, ist deutlich günstiger als eine Individualentwicklung mit Schnittstellen.",
            "Bei mir: Festpreis nach Umfang für den Aufbau, 110 € netto pro Stunde für Anpassungen und Entwicklung, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
        {
          h: "Was den Preis wirklich treibt",
          p: [],
          list: [
            "Anzahl der Produkte und Varianten",
            "Mehrere Sprachen oder Länder",
            "B2B-Preise, Staffeln, Kundengruppen",
            "Schnittstellen zu Warenwirtschaft, Versand oder Buchhaltung",
            "Vollständigkeit Ihrer Produktdaten, Bilder und Texte",
          ],
        },
        {
          h: "Was Sie sich sparen können",
          p: [
            "Individuelle Themes ohne besonderen Anlass, Apps für Funktionen, die das Theme selbst kann, und Sonderwünsche, die vor dem Start noch niemand gebraucht hat. Was nach dem Livegang wirklich fehlt, lässt sich stundenweise nachziehen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten im Detail" },
        { to: "/fragen/shopify-agentur-oder-freelancer", label: "Agentur oder Freelancer?" },
      ]}
    />
  );
}
